import "@/App.css";
import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { 
  ToggleRight, 
  Users, 
  MessageCircle, 
  MapPin, 
  Coffee, 
  Footprints, 
  Smartphone,
  Check,
  ChevronDown,
  Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Female avatar URLs - diverse, natural, candid representation
const femaleAvatars = {
  // Hero social proof avatars - natural candid shots
  socialProof: [
    "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&h=100&fit=crop&crop=face", // Natural blonde casual
    "https://images.unsplash.com/photo-1523824921871-d6f1a15151f1?w=100&h=100&fit=crop&crop=face", // Black woman natural hair laughing
    "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=100&h=100&fit=crop&crop=face", // Brunette natural smile
    "https://images.unsplash.com/photo-1506863530036-1efeddceb993?w=100&h=100&fit=crop&crop=face", // Woman with curly hair candid
  ],
  // Mockup profiles - real candid women
  sarah: "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=80&h=80&fit=crop&crop=face", // Natural blonde
  maya: "https://images.unsplash.com/photo-1523824921871-d6f1a15151f1?w=80&h=80&fit=crop&crop=face", // Black woman laughing  
  aisha: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=80&h=80&fit=crop&crop=face", // Black woman natural smile
  sofia: "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=80&h=80&fit=crop&crop=face", // Latina natural
  emma: "https://images.unsplash.com/photo-1502685104226-ee32379fefbe?w=80&h=80&fit=crop&crop=face", // Redhead candid
  lara: "https://images.unsplash.com/photo-1506863530036-1efeddceb993?w=80&h=80&fit=crop&crop=face", // Curly hair natural
  jessica: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=80&h=80&fit=crop&crop=face", // Natural casual
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Waitlist Form Component
const WaitlistForm = ({ variant = "default" }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [position, setPosition] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/waitlist`, { email, name: name || undefined });
      setSuccess(true);
      setPosition(response.data.position);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl p-6 md:p-8 border border-[#EBE5DA] text-center"
        data-testid="waitlist-success"
      >
        <div className="w-16 h-16 bg-accent-success rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-text-main" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-text-main mb-2">You're in!</h3>
        <p className="text-text-muted mb-2">You're #{position} on the waitlist</p>
        <p className="text-sm text-text-muted">We'll email you when OPEN is ready for you.</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`${variant === "hero" ? "max-w-md mx-auto" : ""}`} data-testid="waitlist-form">
      <div className="flex flex-col gap-3">
        <input
          type="text"
          placeholder="Your name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-white border border-[#EBE5DA] rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-stone-400 font-body"
          data-testid="waitlist-name-input"
        />
        <input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="bg-white border border-[#EBE5DA] rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-stone-400 font-body"
          data-testid="waitlist-email-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="bg-primary text-white rounded-full px-8 py-4 font-bold text-lg hover:bg-primary-hover hover:scale-105 transition-all shadow-lg hover:shadow-orange-500/30 disabled:opacity-60 disabled:cursor-not-allowed btn-glow font-heading"
          data-testid="waitlist-submit-btn"
        >
          {loading ? "Joining..." : "Get Early Access"}
        </button>
      </div>
      {error && <p className="text-red-500 text-sm mt-2 text-center" data-testid="waitlist-error">{error}</p>}
    </form>
  );
};

// Phone Mockup Component
const PhoneMockup = ({ children, className = "" }) => {
  return (
    <div className={`phone-mockup ${className}`}>
      <div className="phone-screen">
        {children}
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  const [waitlistCount, setWaitlistCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/waitlist/count`);
        setWaitlistCount(res.data.count);
      } catch (e) {
        console.log("Could not fetch waitlist count");
      }
    };
    fetchCount();
  }, []);

  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="min-h-screen flex flex-col justify-center relative overflow-hidden px-6 md:px-12 lg:px-20 pt-16 pb-12" data-testid="hero-section">
      <div className="hero-glow" />
      
      <div className="max-w-7xl mx-auto w-full relative z-10">
        <motion.div 
          className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Left Content */}
          <div className="flex-1 text-center lg:text-left">
            <motion.p 
              variants={fadeInUp}
              className="font-accent text-xl text-primary mb-4"
            >
              No more "you free?" loops
            </motion.p>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-heading text-6xl md:text-8xl font-extrabold tracking-tight leading-[1.1] text-primary open-title mb-6"
            >
              OPEN
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl leading-relaxed text-text-muted max-w-lg mb-8"
            >
              See who's free right now. Be visible when you are. No more endless "what's up?" texts – just connect.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="mb-6">
              <button 
                onClick={scrollToWaitlist}
                className="bg-primary text-white rounded-full px-10 py-5 font-bold text-lg hover:bg-primary-hover transition-all shadow-lg hover:shadow-orange-500/30 btn-glow font-heading"
                data-testid="hero-cta-btn"
              >
                Request Early Access
              </button>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="flex items-center justify-center lg:justify-start gap-4">
              <div className="avatar-stack flex">
                {femaleAvatars.socialProof.map((url, i) => (
                  <img 
                    key={i}
                    src={url} 
                    alt="User" 
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ))}
              </div>
              <p className="text-sm text-text-muted">
                <span className="font-semibold text-text-main">150+</span> women already joined
              </p>
            </motion.div>
          </div>
          
          {/* Right - Phone Mockup */}
          <motion.div 
            variants={fadeInUp}
            className="flex-1 flex justify-center"
          >
            <PhoneMockup className="w-[280px] md:w-[320px] float-animation">
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-heading font-bold text-lg text-text-main">OPEN</span>
                  <span className="text-xs text-text-muted">9:41 AM</span>
                </div>
                
                <div className="bg-background rounded-2xl p-4 mb-6">
                  <p className="text-sm text-text-muted mb-3">Your status</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-accent-success rounded-full flex items-center justify-center">
                        <Zap className="w-6 h-6 text-text-main" />
                      </div>
                      <div>
                        <p className="font-heading font-bold text-text-main">I'm Open!</p>
                        <p className="text-xs text-text-muted">Ready to hang</p>
                      </div>
                    </div>
                    <div className="toggle-switch active" />
                  </div>
                </div>
                
                <p className="text-sm text-text-muted mb-3">Nearby & Open</p>
                {[
                  { name: "Sarah", distance: "0.3 mi", interest: "coffee", icon: Coffee, avatar: femaleAvatars.sarah },
                  { name: "Maya", distance: "0.5 mi", interest: "walk", icon: Footprints, avatar: femaleAvatars.maya },
                ].map((person, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-[#EBE5DA] last:border-0">
                    <div className="flex items-center gap-3">
                      <img src={person.avatar} alt={person.name} className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <p className="font-heading font-semibold text-text-main">{person.name}</p>
                        <p className="text-xs text-text-muted flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {person.distance}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                      <person.icon className="w-3 h-3" />
                      {person.interest}
                    </div>
                  </div>
                ))}
              </div>
            </PhoneMockup>
          </motion.div>
        </motion.div>
      </div>
      
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <ChevronDown className="w-6 h-6 text-text-muted animate-bounce" />
      </motion.div>
    </section>
  );
};

// Problem Section
const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  return (
    <section ref={ref} className="py-20 md:py-32 px-6 md:px-12 lg:px-20 bg-background-paper" data-testid="problem-section">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-text-main mb-8"
          >
            We live online, but we still hesitate.
          </motion.h2>
          
          <motion.p 
            variants={fadeInUp}
            className="text-lg md:text-xl leading-relaxed text-text-muted mb-12 max-w-2xl mx-auto"
          >
            We send "hey, what's up?" and ghost each other. We overthink reaching out when someone's actually free right now.
          </motion.p>
          
          <motion.div 
            variants={fadeInUp}
            className="flex flex-wrap justify-center gap-8"
          >
            {[
              { icon: MessageCircle, label: "Endless texting", desc: '"hey wyd?" ... *seen*' },
              { icon: Smartphone, label: "Scrolling alone", desc: "Everyone's online, nobody's out" },
              { icon: Users, label: "Missing moments", desc: "Friends were nearby the whole time" },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-3 max-w-[160px]">
                <div className="w-16 h-16 bg-background rounded-2xl flex items-center justify-center border border-[#EBE5DA]">
                  <item.icon className="w-8 h-8 text-text-muted" />
                </div>
                <p className="font-heading font-semibold text-text-main">{item.label}</p>
                <p className="text-sm text-text-muted">{item.desc}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Solution Section
const SolutionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  const features = [
    { icon: ToggleRight, title: "One-tap availability", desc: 'Turn your status to "Open" in one tap' },
    { icon: Users, title: "See who's Open", desc: "See your friends or nearby women who are Open right now" },
    { icon: MessageCircle, title: "Connect instantly", desc: 'No awkward "you free?" texts needed' },
    { icon: Zap, title: "No pressure", desc: "No commitment – just real-time availability" },
  ];

  return (
    <section ref={ref} className="py-20 md:py-32 px-6 md:px-12 lg:px-20" data-testid="solution-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-text-main text-center mb-4"
          >
            OPEN removes the guesswork.
          </motion.h2>
          
          <motion.p 
            variants={fadeInUp}
            className="text-lg text-text-muted text-center mb-16 max-w-2xl mx-auto"
          >
            Stop wondering. Start connecting.
          </motion.p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <motion.div 
                key={i}
                variants={fadeInUp}
                className="bg-white rounded-3xl p-6 border border-[#EBE5DA] shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 feature-card"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading font-bold text-lg text-text-main mb-2">{feature.title}</h3>
                <p className="text-text-muted text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
          
          {/* Mockups Row */}
          <motion.div 
            variants={fadeInUp}
            className="mt-16 flex flex-wrap justify-center gap-8"
          >
            {/* Toggle Mockup */}
            <PhoneMockup className="w-[200px] md:w-[220px]">
              <div className="p-4">
                <p className="text-xs text-text-muted text-center mb-4">Set your status</p>
                <div className="bg-accent-success/20 rounded-2xl p-4 text-center">
                  <div className="w-20 h-20 bg-accent-success rounded-full flex items-center justify-center mx-auto mb-3">
                    <Zap className="w-10 h-10 text-text-main" />
                  </div>
                  <p className="font-heading font-bold text-text-main">I'm Open!</p>
                  <div className="toggle-switch active mx-auto mt-3" />
                </div>
              </div>
            </PhoneMockup>
            
            {/* List Mockup */}
            <PhoneMockup className="w-[200px] md:w-[220px]">
              <div className="p-4">
                <p className="text-xs text-text-muted text-center mb-4">Nearby & Open</p>
                {[
                  { name: "Sofia", dist: "0.2 mi", tag: "coffee", avatar: femaleAvatars.sofia },
                  { name: "Emma", dist: "0.4 mi", tag: "walk", avatar: femaleAvatars.emma },
                  { name: "Lara", dist: "0.8 mi", tag: "lunch", avatar: femaleAvatars.lara },
                ].map((p, i) => (
                  <div key={i} className="flex items-center gap-2 py-2 border-b border-[#EBE5DA] last:border-0">
                    <img src={p.avatar} alt={p.name} className="w-8 h-8 rounded-full object-cover" />
                    <div className="flex-1">
                      <p className="font-heading font-semibold text-sm text-text-main">{p.name}</p>
                      <p className="text-xs text-text-muted">{p.dist}</p>
                    </div>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{p.tag}</span>
                  </div>
                ))}
              </div>
            </PhoneMockup>
            
            {/* Chat Mockup */}
            <PhoneMockup className="w-[200px] md:w-[220px]">
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#EBE5DA]">
                  <img src={femaleAvatars.aisha} alt="Aisha" className="w-6 h-6 rounded-full object-cover" />
                  <p className="text-xs font-heading font-semibold text-text-main">Aisha</p>
                </div>
                <div className="space-y-2">
                  <div className="bg-background rounded-xl rounded-bl-sm p-3 max-w-[80%]">
                    <p className="text-sm text-text-main">Hey girl! You're open for coffee?</p>
                  </div>
                  <div className="bg-primary text-white rounded-xl rounded-br-sm p-3 max-w-[80%] ml-auto">
                    <p className="text-sm">Yes! Blue Bottle on Main?</p>
                  </div>
                  <div className="bg-background rounded-xl rounded-bl-sm p-3 max-w-[80%]">
                    <p className="text-sm text-text-main">Perf, see you in 10!</p>
                  </div>
                </div>
              </div>
            </PhoneMockup>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  const steps = [
    { 
      icon: ToggleRight, 
      title: "Set status", 
      desc: "Toggle Open when you're free to hang out with your girls",
      color: "bg-accent-success"
    },
    { 
      icon: MapPin, 
      title: "Discover nearby", 
      desc: "See who's also Open around you right now",
      color: "bg-accent-warning"
    },
    { 
      icon: MessageCircle, 
      title: "Meet spontaneously", 
      desc: "Chat and meet up – no awkward back-and-forth needed",
      color: "bg-primary/20"
    },
  ];

  return (
    <section ref={ref} className="py-20 md:py-32 px-6 md:px-12 lg:px-20 bg-background-paper" data-testid="how-it-works-section">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.p 
            variants={fadeInUp}
            className="font-accent text-xl text-primary text-center mb-2"
          >
            Simple as 1-2-3
          </motion.p>
          
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-text-main text-center mb-16"
          >
            How it works
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <motion.div 
                key={i}
                variants={fadeInUp}
                className="bg-white rounded-3xl p-8 border border-[#EBE5DA] text-center feature-card"
              >
                <div className={`w-20 h-20 ${step.color} rounded-full flex items-center justify-center mx-auto mb-6`}>
                  <step.icon className="w-10 h-10 text-text-main" />
                </div>
                <span className="font-accent text-3xl text-primary">{i + 1}</span>
                <h3 className="font-heading font-bold text-xl text-text-main mt-2 mb-3">{step.title}</h3>
                <p className="text-text-muted">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Footer Section
const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  return (
    <section ref={ref} id="waitlist" className="py-20 md:py-32 px-6 md:px-12 lg:px-20 relative" data-testid="cta-section">
      <div className="hero-glow" />
      
      <div className="max-w-2xl mx-auto text-center relative z-10">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-text-main mb-4"
          >
            Ready to stop overthinking?
          </motion.h2>
          
          <motion.p 
            variants={fadeInUp}
            className="text-lg text-text-muted mb-8"
          >
            Join the waitlist and be the first to know when OPEN launches.
          </motion.p>
          
          <motion.div variants={fadeInUp}>
            <WaitlistForm variant="footer" />
          </motion.div>
          
          <motion.p 
            variants={fadeInUp}
            className="text-sm text-text-muted mt-8"
          >
            iOS & Android coming soon. Built for women who want real connections in NJ/NY first.
          </motion.p>
          
          <motion.div 
            variants={fadeInUp}
            className="flex items-center justify-center gap-4 mt-6"
          >
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <Smartphone className="w-4 h-4" />
              <span>iOS</span>
            </div>
            <div className="w-1 h-1 bg-text-muted rounded-full" />
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <Smartphone className="w-4 h-4" />
              <span>Android</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-8 px-6 md:px-12 lg:px-20 border-t border-[#EBE5DA]" data-testid="footer">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="font-heading font-bold text-xl text-text-main">OPEN</p>
        <p className="text-sm text-text-muted">
          © {new Date().getFullYear()} OPEN. Built for spontaneous connections.
        </p>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="min-h-screen bg-background">
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <HowItWorksSection />
      <CTASection />
      <Footer />
    </div>
  );
}

export default App;
